import { useCallback, useEffect, useRef, useState } from "react"
import { Link } from "react-router-dom"
import Api from "../api/api"
import { useSku } from "../redux/skusSlice"
import {
  Entitlement,
  EntitlementOrigin,
  EntitlementStatus,
  entitlementOriginLabels,
  LicenseType,
  licenseTypeLabels,
} from "../api/models"
import UserChip from "./widgets/UserChip"
import Spinner from "./widgets/Spinner"
import { backdropDismiss } from "../utils/backdropDismiss"

const PAGE_SIZE = 25

const statusBadgeClass: Record<EntitlementStatus, string> = {
  [EntitlementStatus.Active]: "status-active",
  [EntitlementStatus.Revoked]: "status-expired",
  [EntitlementStatus.Superseded]: "status-paused",
}

function formatDate(d?: Date | string | null) {
  if (!d) return null
  const date = typeof d === "string" ? new Date(d) : d
  return date.toLocaleDateString()
}

// Confirmation dialog for revoking one entitlement. Revocation also deletes
// the licenses (and activations) the entitlement granted — the warning text
// spells that out. Live-subscription entitlements are rejected server-side;
// the error message from the backend is shown as-is.
function RevokeDialog({ entitlement, accessToken, onClose, onRevoked }: {
  entitlement: Entitlement
  accessToken: string
  onClose: () => void
  onRevoked: (entitlement: Entitlement, deletedLicenseIds: string[]) => void
}) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [reason, setReason] = useState("")
  const [step, setStep] = useState<"confirm" | "revoking" | "error" | "success">("confirm")
  const [errorMessage, setErrorMessage] = useState("")
  const [deletedCount, setDeletedCount] = useState(0)

  useEffect(() => {
    dialogRef.current?.showModal()
  }, [])

  const handleRevoke = async () => {
    setStep("revoking")
    setErrorMessage("")
    try {
      const result = await Api.revokeEntitlement({
        entitlementId: entitlement.id,
        reason: reason.trim() || null,
        accessToken,
      })
      onRevoked(result.entitlement, result.deletedLicenseIds)
      setDeletedCount(result.deletedLicenseIds.length)
      setStep("success")
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "unknown error")
      setStep("error")
    }
  }

  return (
    <dialog
      ref={dialogRef}
      className="redeem-dialog"
      onClose={onClose}
      {...backdropDismiss(() => { if (step !== "revoking") dialogRef.current?.close() })}
    >
      <div className="redeem-dialog-content">
        <button className="redeem-dialog-close" onClick={() => dialogRef.current?.close()} aria-label="Close">&times;</button>
        <h2 className="redeem-dialog-title">Revoke Entitlement</h2>

        {step !== "success" && (
          <>
            <p>
              This revokes <strong>{entitlement.userId}</strong>'s{" "}
              {entitlementOriginLabels[entitlement.origin].toLowerCase()} entitlement and{" "}
              <strong>deletes the licenses and activations</strong> it granted. This cannot be undone.
            </p>
            <div className="generate-codes-form">
              <label className="generate-codes-field">
                <span className="generate-codes-label">Reason (optional)</span>
                <input
                  type="text"
                  maxLength={255}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="generate-codes-input"
                  disabled={step === "revoking"}
                  placeholder="e.g. chargeback"
                  autoFocus
                />
              </label>
            </div>

            {step === "error" && errorMessage && (
              <p className="generate-codes-error">{errorMessage}</p>
            )}

            <div className="redeem-actions">
              <button onClick={() => dialogRef.current?.close()} disabled={step === "revoking"}>Cancel</button>
              <button
                className="btn-upgrade"
                disabled={step === "revoking"}
                onClick={handleRevoke}
              >
                {step === "revoking" ? <><Spinner size={12} /> Revoking…</> : "Revoke"}
              </button>
            </div>
          </>
        )}

        {step === "success" && (
          <>
            <p className="generate-codes-success">
              Entitlement revoked. <strong>{deletedCount}</strong> license{deletedCount === 1 ? "" : "s"} deleted.
            </p>
            <div className="redeem-actions">
              <button className="btn-upgrade" onClick={() => dialogRef.current?.close()}>Done</button>
            </div>
          </>
        )}
      </div>
    </dialog>
  )
}

// grant/reward have no detail pages (yet) — those origins render as text
const originDetailPath: Partial<Record<EntitlementOrigin, (refId: string) => string>> = {
  [EntitlementOrigin.Order]: (refId) => `/orders/${refId}`,
  [EntitlementOrigin.Code]: (refId) => `/redemptions/${refId}`,
  [EntitlementOrigin.Subscription]: (refId) => `/subscriptions/${refId}`,
}

function OriginCell({ entitlement }: { entitlement: Entitlement }) {
  const pathFor = originDetailPath[entitlement.origin]
  const label = entitlementOriginLabels[entitlement.origin]
  return (
    <td title={entitlement.refId}>
      {pathFor ? (
        <Link to={pathFor(entitlement.refId)} className="card-link">{label}</Link>
      ) : (
        label
      )}
    </td>
  )
}

// the license type its deposited licenses carry — its own column (it used to
// piggyback on Origin, shown only when non-standard); Standard renders muted
// so the exceptional types stand out
// An entitlement issued iLok-disabled (grant / code batch switch) says so
// next to its type — the holder gets no "Get iLok license".
function TypeCell({ entitlement }: { entitlement: Entitlement }) {
  const standard = entitlement.licenseType === LicenseType.Standard
  return (
    <td style={standard ? { color: "var(--nfa-light-grey)" } : undefined}>
      {licenseTypeLabels[entitlement.licenseType]}
      {entitlement.ilokEnabled === false && (
        <span title="Issued without iLok — the holder can't request an iLok license" style={{ color: "var(--nfa-light-grey)", marginLeft: 6 }}>
          · no iLok
        </span>
      )}
    </td>
  )
}

// resolves the sku name via the cached skus slice; falls back to the raw id
// (the owned version has its own column)
function SkuCell({ skuId }: { skuId: string }) {
  const { data: sku } = useSku(skuId)
  return (
    <td title={skuId}>
      <Link to={`/skus/${skuId}`} className="card-link">{sku?.name ?? skuId}</Link>
    </td>
  )
}

// The entitlement's end date — perpetual rows show a dash; a date already
// passed is flagged, since expiry is read-time and the status stays active
function ExpiresCell({ entitlement }: { entitlement: Entitlement }) {
  if (!entitlement.expiresAt) return <td>—</td>
  const expired = new Date(entitlement.expiresAt).getTime() < Date.now()
  return (
    <td>
      {formatDate(entitlement.expiresAt)}
      {expired && <div style={{ color: "var(--nfa-light-grey)", fontSize: "9pt" }}>expired</div>}
    </td>
  )
}

function EntitlementRow({ entitlement, showUserColumn, showSkuColumn, onRevoke }: {
  entitlement: Entitlement
  showUserColumn: boolean
  showSkuColumn: boolean
  onRevoke: (entitlement: Entitlement) => void
}) {
  return (
    <tr className={entitlement.status === EntitlementStatus.Active ? undefined : "row-muted"}>
      {showUserColumn && (
        <td>
          <UserChip userId={entitlement.userId} />
        </td>
      )}
      {showSkuColumn && <SkuCell skuId={entitlement.skuId} />}
      {/* the owned version: one-time/code/grant entitlements stay pinned to
          it, subscription ones auto-advance to the sku's latest. The != null
          guards a backend that predates versioned claims */}
      <td>{entitlement.skuVersion != null ? `v${entitlement.skuVersion}` : "—"}</td>
      <TypeCell entitlement={entitlement} />
      <OriginCell entitlement={entitlement} />
      <td>
        <span className={`status-badge ${statusBadgeClass[entitlement.status]}`}>
          {entitlement.status}
        </span>
      </td>
      <td>{formatDate(entitlement.createdAt)}</td>
      <ExpiresCell entitlement={entitlement} />
      <td>
        {entitlement.status === EntitlementStatus.Revoked ? (
          <>
            {formatDate(entitlement.revokedAt) ?? "—"}
            {(entitlement.revokedBy || entitlement.revokedReason) && (
              <div style={{ color: "var(--nfa-light-grey)", fontSize: "9pt" }}>
                {entitlement.revokedBy && <>by {entitlement.revokedBy}</>}
                {entitlement.revokedReason && <> ({entitlement.revokedReason})</>}
              </div>
            )}
          </>
        ) : (
          "—"
        )}
      </td>
      <td className="text-right">
        {entitlement.status === EntitlementStatus.Active && (
          <button
            className="card-link-danger"
            style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
            onClick={() => onRevoke(entitlement)}
          >
            Revoke
          </button>
        )}
      </td>
    </tr>
  )
}

// Paginated entitlements table + revoke flow, scoped by any AND-combination of
// skuId/userId/origin/status. State is local: the shared paginatedCollection
// redux helpers are singletons — they can't be keyed by sku or user, and
// local state resets naturally on navigation or scope change.
//
// previewCount switches the table to preview mode: fetch only that many rows
// and, when more exist, link to morePath (the full list) instead of paging
// in place.
export default function EntitlementsTable({ skuId, userId, origin, status, showUserColumn = false, showSkuColumn = false, previewCount, morePath, accessToken }: {
  skuId?: string | null
  userId?: string | null
  origin?: EntitlementOrigin | null
  status?: EntitlementStatus | null
  showUserColumn?: boolean
  showSkuColumn?: boolean
  previewCount?: number
  morePath?: string
  accessToken: string
}) {
  const [entitlements, setEntitlements] = useState<Entitlement[] | undefined>(undefined)
  const [link, setLink] = useState<string | null>(null)
  // from the first page of the current scope; undefined until the backend
  // ships totals
  const [totalUsers, setTotalUsers] = useState<number | undefined>(undefined)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [revokeTarget, setRevokeTarget] = useState<Entitlement | null>(null)
  // stale responses (scope changed mid-flight) must not land in state
  const requestSeq = useRef(0)

  const pageSize = previewCount ?? PAGE_SIZE

  const loadPage = useCallback(async (cursor: string | null) => {
    const seq = ++requestSeq.current
    setLoading(true)
    setError(false)
    try {
      const page = await Api.listEntitlements({
        skuId: skuId ?? null,
        userId: userId ?? null,
        origin: origin ?? null,
        status: status ?? null,
        link: cursor,
        count: pageSize,
        accessToken,
      })
      if (seq !== requestSeq.current) return
      setEntitlements((prev) => (cursor === null ? page.entitlements : [...(prev ?? []), ...page.entitlements]))
      setLink(page.link)
      if (cursor === null) setTotalUsers(page.totalUsers)
    } catch {
      if (seq === requestSeq.current) setError(true)
    } finally {
      if (seq === requestSeq.current) setLoading(false)
    }
  }, [skuId, userId, origin, status, pageSize, accessToken])

  useEffect(() => {
    setEntitlements(undefined)
    setLink(null)
    setTotalUsers(undefined)
    loadPage(null)
  }, [loadPage])

  const haveMore = link !== null

  if (error && entitlements === undefined) {
    return <p className="empty-state">Error loading entitlements</p>
  }

  if (entitlements === undefined) {
    return <p className="empty-state"><Spinner size={16} /> Loading entitlements…</p>
  }

  // headerless empty state — a lone header row over "no results" is just noise
  if (entitlements.length === 0) {
    return <p className="empty-state">None</p>
  }

  return (
    <>
      <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              {showUserColumn && <th>User</th>}
              {showSkuColumn && <th>SKU</th>}
              <th>Version</th>
              <th>Type</th>
              <th>Origin</th>
              <th>Status</th>
              <th>Created</th>
              <th>Expires</th>
              <th>Revoked</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {entitlements.map((entitlement) => (
              <EntitlementRow
                key={entitlement.id}
                entitlement={entitlement}
                showUserColumn={showUserColumn}
                showSkuColumn={showSkuColumn}
                onRevoke={setRevokeTarget}
              />
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ marginLeft: "8px", display: "flex", alignItems: "baseline", gap: 12 }}>
        {/* {totalUsers !== undefined && (
          <span style={{ color: "var(--nfa-light-grey)", fontSize: "10pt" }}>
            {totalUsers} user{totalUsers === 1 ? "" : "s"}
          </span>
        )} */}
        {haveMore && (
          previewCount != null ? (
            morePath != null && (
              <Link to={morePath} className="card-link">More</Link>
            )
          ) : loading ? <Spinner size={16} /> : (
            <button className="card-link" onClick={() => loadPage(link)} style={{ background: "none", border: "none", cursor: "pointer" }}>
              Load more
            </button>
          )
        )}
        {error && (
          <p className="empty-state">Error loading more entitlements</p>
        )}
      </div>

      {revokeTarget && (
        <RevokeDialog
          entitlement={revokeTarget}
          accessToken={accessToken}
          onClose={() => setRevokeTarget(null)}
          onRevoked={(revoked) =>
            setEntitlements((prev) => prev?.map((e) => (e.id === revoked.id ? revoked : e)))
          }
        />
      )}
    </>
  )
}
